/**
 * PrinterAutoConnect
 * ==================
 * Headless al login, esegue 3 azioni:
 *  1. Riconnette SILENZIOSAMENTE tutte le stampanti USB/BT già pairate (via getDevices()).
 *  2. Se almeno una stampante configurata in DB non risulta "vista" da questo browser,
 *     mostra un popup "Ricollega stampanti" con un pulsante "Collega" per ognuna (apre il picker).
 *  3. Il popup è mostrato UNA VOLTA per sessione (sessionStorage) e all'avvio dell'app.
 *
 * Limite browser: `navigator.usb.requestDevice()` richiede un click umano per ogni stampante.
 * Non possiamo bypassare. La UX ottimizzata è cliccare "Collega" per ciascuna e procedere.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { webusb, webbt } from "@/lib/browserPrinting";
import { Printer, Usb, Bluetooth, CheckCircle2, X, Loader2, RefreshCw } from "lucide-react";

const RECONNECT_FLAG = "smarttable_printers_reconnect_shown_v1";

const PrinterAutoConnect = () => {
    const { user } = useAuth();
    const [show, setShow] = useState(false);
    const [missing, setMissing] = useState([]);
    const [connectingId, setConnectingId] = useState(null);

    // Carica stampanti e verifica quali NON sono pairate in questo browser
    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/command-printers");
            const needPairing = [];
            const pairedNow = [];
            for (const p of data) {
                const proto = (p.protocol || "").toLowerCase();
                if (proto === "usb" && p.usb_vendor_id && p.usb_product_id) {
                    const dev = webusb.supported()
                        ? await webusb.findPairedDevice(p.usb_vendor_id, p.usb_product_id)
                        : null;
                    if (dev) pairedNow.push(p); else needPairing.push(p);
                } else if (proto === "bluetooth" && p.bt_device_id) {
                    const dev = webbt.supported()
                        ? await webbt.findPairedDevice(p.bt_device_id)
                        : null;
                    if (dev) pairedNow.push(p); else needPairing.push(p);
                }
                // TCP: nessun pairing necessario, skip
            }
            return { needPairing, pairedNow, total: data.length };
        } catch (e) {
            console.error("[PrinterAutoConnect] load failed", e);
            return { needPairing: [], pairedNow: [], total: 0 };
        }
    }, []);

    useEffect(() => {
        // Solo admin e camerieri (chi opera il terminale)
        if (!user || !["admin", "cameriere", "cassa"].includes(user.role)) return;
        // Mostra solo se non già mostrato in questa sessione (apertura browser)
        if (sessionStorage.getItem(RECONNECT_FLAG)) return;

        let cancelled = false;
        const run = async () => {
            const { needPairing, pairedNow } = await refresh();
            if (cancelled) return;
            if (pairedNow.length > 0) {
                console.log(`[PrinterAutoConnect] ${pairedNow.length} stampanti già pronte (auto-reconnect)`);
            }
            if (needPairing.length > 0) {
                setMissing(needPairing);
                setShow(true);
            }
            sessionStorage.setItem(RECONNECT_FLAG, "1");
        };
        // Aspetta 1.5s dal login per non sovrapporsi ad altri toast
        const t = setTimeout(run, 1500);

        // Ascolta richiesta esplicita "Riconnetti tutto" da /stampanti
        const onForce = async () => {
            const { needPairing } = await refresh();
            setMissing(needPairing);
            setShow(needPairing.length > 0);
        };
        window.addEventListener("smarttable:reconnect-printers", onForce);

        return () => {
            cancelled = true;
            clearTimeout(t);
            window.removeEventListener("smarttable:reconnect-printers", onForce);
        };
    }, [user, refresh]);

    const pairUsb = async (printer) => {
        setConnectingId(printer.id);
        try {
            const info = await webusb.requestDevice();
            if (info.vendorId !== printer.usb_vendor_id || info.productId !== printer.usb_product_id) {
                // Aggiorna il record con i nuovi ID se la stampante è cambiata fisicamente
                if (window.confirm(
                    `Hai selezionato una stampante con ID diverso (VID=${info.vendorId.toString(16)}/PID=${info.productId.toString(16)}). ` +
                    `Aggiornare il record "${printer.name}" con questi nuovi ID?`
                )) {
                    await api.patch(`/command-printers/${printer.id}`, {
                        usb_vendor_id: info.vendorId,
                        usb_product_id: info.productId,
                    });
                } else {
                    toast.error("Pairing annullato: ID non corrispondenti");
                    setConnectingId(null);
                    return;
                }
            }
            toast.success(`${printer.name} collegata`);
            setMissing((prev) => prev.filter((p) => p.id !== printer.id));
        } catch (e) {
            if (String(e?.message || "").toLowerCase().includes("no device selected")) {
                toast.info("Selezione annullata");
            } else {
                toast.error(formatError(e));
            }
        } finally {
            setConnectingId(null);
        }
    };

    const pairBt = async (printer) => {
        setConnectingId(printer.id);
        try {
            const info = await webbt.requestDevice();
            if (info.deviceId !== printer.bt_device_id) {
                if (window.confirm(`Aggiornare il record "${printer.name}" con questo nuovo device Bluetooth?`)) {
                    await api.patch(`/command-printers/${printer.id}`, { bt_device_id: info.deviceId });
                } else {
                    setConnectingId(null);
                    return;
                }
            }
            toast.success(`${printer.name} collegata`);
            setMissing((prev) => prev.filter((p) => p.id !== printer.id));
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setConnectingId(null);
        }
    };

    const reconnectAll = async () => {
        toast.info("Riverifico le stampanti…");
        const { needPairing } = await refresh();
        setMissing(needPairing);
        if (needPairing.length === 0) {
            toast.success("Tutte le stampanti sono pronte ✅");
            setShow(false);
        }
    };

    const closeModal = () => setShow(false);

    if (!show || missing.length === 0) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4" data-testid="reconnect-printers-modal">
            <div className="bg-surface border-2 border-brand/60 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-md bg-brand/20 border border-brand/40 flex items-center justify-center flex-shrink-0">
                            <Printer className="w-6 h-6 text-brand" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-brand">Avvio sistema</div>
                            <h2 className="font-display text-xl font-bold leading-tight">Ricollega stampanti</h2>
                            <p className="text-xs text-elegant mt-1">
                                {missing.length} stampant{missing.length === 1 ? "e" : "i"} configurat{missing.length === 1 ? "a" : "e"} {missing.length === 1 ? "non è ancora collegata" : "non sono ancora collegate"} a questo PC.
                                Per ognuna premi <strong className="text-warm">Collega</strong> e seleziona il dispositivo nel popup del browser.
                            </p>
                        </div>
                    </div>
                    <button onClick={closeModal} className="text-elegant hover:text-warm" data-testid="reconnect-skip">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-2 mt-5">
                    {missing.map((p) => {
                        const isUsb = (p.protocol || "").toLowerCase() === "usb";
                        const isConnecting = connectingId === p.id;
                        const Icon = isUsb ? Usb : Bluetooth;
                        return (
                            <div key={p.id} className="flex items-center gap-3 bg-deep border border-[#1f2c44] rounded-md p-3" data-testid={`reconnect-row-${p.id}`}>
                                <Icon className={`w-5 h-5 ${isUsb ? "text-amber-300" : "text-cyan-300"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-warm text-sm truncate">{p.name}</div>
                                    <div className="text-[11px] text-elegant truncate">
                                        {p.destination} · {p.brand} {p.model}
                                    </div>
                                </div>
                                <button
                                    onClick={() => (isUsb ? pairUsb(p) : pairBt(p))}
                                    disabled={isConnecting}
                                    data-testid={`reconnect-pair-${p.id}`}
                                    className="h-10 px-4 bg-brand text-deep font-bold text-xs uppercase tracking-wider rounded flex items-center gap-2 disabled:opacity-50"
                                >
                                    {isConnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                    {isConnecting ? "…" : "Collega"}
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="flex gap-2 mt-5">
                    <button
                        onClick={reconnectAll}
                        data-testid="reconnect-verify-all"
                        className="flex-1 h-12 border border-brand text-brand rounded-md flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-bold hover:bg-brand/10"
                    >
                        <RefreshCw className="w-4 h-4" /> Riverifica
                    </button>
                    <button
                        onClick={closeModal}
                        data-testid="reconnect-done"
                        className="flex-1 h-12 bg-deep border border-[#1f2c44] text-elegant rounded-md flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-bold hover:border-warm hover:text-warm"
                    >
                        Continua senza
                    </button>
                </div>

                <div className="mt-4 text-[11px] text-elegant italic text-center">
                    Suggerimento: questo popup appare solo una volta per sessione, all'avvio del browser.
                </div>
            </div>
        </div>
    );
};

export default PrinterAutoConnect;
